/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.23.0
 */

import { Fragment, useCallback, useRef, useState } from 'react';

import { MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { MutationHooks } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';

import { IInfiniteMutation, useInfiniteMutation } from '@/main/api/useInfiniteMutation';
import { MultiSelect } from '@components/common';
import { DropDownFilterButtonSize } from '@components/common/DropDownFilter/DropDownFilter';
import { FormLabelStyled } from '@components/common/FormStyled/FormStyled';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import { IMultiSelectProps } from '@components/common/MultiSelect/MultiSelect';
import { IOptionItemProps } from '@components/common/Select/Select';
import { ITEMS_MIN_COUNT_FOR_SEARCH_VISIBILITY } from '@modules/form/const/formConstants';

import { ICreateInquiryAttributeDictionary } from '../../inquiry/types';
import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import FormField, { IFormFieldProps } from './FormField';

type TFormFieldInfinite<Params extends Record<string, unknown>> = {
    endpoint: MutationHooks<
        MutationDefinition<IInfiniteMutation & Params, any, string, ICreateInquiryAttributeDictionary>
    >;
    params: Params;
};

type TFormFieldMultiSelectInfinite<Params extends Record<string, unknown>> = IFormFieldProps &
    Partial<Pick<IMultiSelectProps, 'onSelect' | 'fullWide'>> &
    TFormFieldInfinite<Params>;

export const FormFieldMultiSelectInfinite = <Params extends Record<string, unknown>>({
    endpoint,
    params,
    onSelect,
    fullWide,
    ...props
}: TFormFieldMultiSelectInfinite<Params>) => {
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const { setFieldTouched, setFieldValue, validateField } = useFormikContext();

    const { data, isLoading, fetchNextPage, hasMore, count } =
        useInfiniteMutation<IOptionItemProps, IInfiniteMutation & Params>(endpoint);

    const [searchString, setSearchString] = useState('');
    const refSearchString = useRef('');

    const onOpenAndClose = (isOpen: boolean) => {
        if (!isOpen) return;
        fetchNextPage({ searchValue: '', start: true, ...params });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const delayedStartSearch = useCallback(
        debounce((searchValue: string) => {
            fetchNextPage({ searchValue, start: true, ...params });
        }, 500),
        [fetchNextPage],
    );

    const handleSearchChange = (searchValue: string) => {
        setSearchString(() => {
            refSearchString.current = searchValue;
            return searchValue;
        });

        delayedStartSearch(searchValue);
    };

    const virtualListProps = {
        isLoading,
        hasMore,
        onScrollEvent: () => {
            fetchNextPage({ searchValue: refSearchString.current, start: false, ...params });
        },
        rowHeight: 32,
    };

    return (
        <FormField {...props}>
            {({ field, label, required, kind, placeholder }) => {
                const onSelectHandler = (value: IOptionItemProps[]) => {
                    onSelect?.(value);
                    setFieldValue(field.name, value);
                    if (searchString) {
                        fetchNextPage({ searchValue: '', start: true, ...params });
                        setSearchString(() => {
                            refSearchString.current = '';
                            return '';
                        });
                    }
                    setTimeout(() => {
                        validateField(field.name);
                    });
                };

                return (
                    <Fragment>
                        {label && (
                            <FormLabelStyled>
                                {label}
                                {required && <LabelMarkStyled>*</LabelMarkStyled>}
                            </FormLabelStyled>
                        )}
                        <MultiSelect
                            filterButtonSize={DropDownFilterButtonSize.medium}
                            fullWide={fullWide}
                            isSuggest={false}
                            kind={kind}
                            options={data}
                            placeholder={placeholder}
                            selectedOptions={field.value}
                            showSearch={count > ITEMS_MIN_COUNT_FOR_SEARCH_VISIBILITY}
                            translation={translationCommon}
                            virtualListProps={virtualListProps}
                            onClose={() => setFieldTouched(field.name)}
                            onOpenAndClose={onOpenAndClose}
                            onSearchInputChange={handleSearchChange}
                            onSelect={onSelectHandler}
                        />
                    </Fragment>
                );
            }}
        </FormField>
    );
};
